import type { QueryClient } from '@tanstack/react-query';
import { configureOutbox } from '@kidshome/api-contract';
import { outboxController } from './outbox-controller';
import { invalidationKeysFor } from './outbox-invalidation';

/**
 * Wire the offline outbox into the running app (app `shared` layer). Called once at startup with the
 * app's QueryClient, a getter for the signed-in member's id (the shared-device ownership gate —
 * entries are stamped with their enqueuing member and only ever replay under that member's session),
 * and a getter for the live platform-owner impersonation scope (ADR-0055 — a write enqueued in one
 * household's acting-as scope only replays while the owner is acting in that same household):
 *   1. attach the controller (QueryClient + the URL→query-key invalidation mapper + owner getter),
 *   2. register the controller as the api-contract outbox bridge (so failed-offline writes queue),
 *   3. install the replay triggers — app-driven replay is the PRIMARY mechanism since iOS has no
 *      Background Sync: `online`, `visibilitychange`→visible, window `focus`, and `pageshow` (bfcache
 *      restore) all drain the queue.
 *   4. install a poll as a fallback trigger (see {@link REPLAY_POLL_INTERVAL_MS}) for when none of the
 *      above fire — a real gap in practice, not a hypothetical one.
 *   5. best-effort `navigator.storage.persist()` so iOS is less likely to evict the (rebuildable)
 *      outbox mid-session.
 *
 * Returns a teardown that removes the listeners (used by tests; the app keeps it for the tab's life).
 */

/**
 * Fallback trigger: the `online`/`visibilitychange`/`focus`/`pageshow` events above are the PRIMARY
 * replay triggers but are known to be unreliable in practice (most acutely on iOS Safari standalone
 * PWAs). If a user is already foregrounded when connectivity returns and `online` doesn't fire — or
 * fires but the first drain attempt hits one transient blip, and `replay()` deliberately stops the
 * drain "for the next trigger" to avoid hot-looping — nothing else re-checks the queue, and the
 * pending badge looks stuck forever. This bounded, cheap poll is the self-healing backstop.
 */
const REPLAY_POLL_INTERVAL_MS = 20_000;

export function installOutbox(
  queryClient: QueryClient,
  getOwnerId: () => string | null,
  getActingAsHouseholdId: () => string | null = () => null,
): () => void {
  outboxController.attach(queryClient, invalidationKeysFor, getOwnerId, getActingAsHouseholdId);
  configureOutbox(outboxController);

  // Re-foregrounding fires focus + pageshow + visibilitychange + online in one burst, each of which
  // would otherwise kick its own drain — multiplying transient retries during a brief server/auth
  // outage and burning an entry's MAX_REPLAY_ATTEMPTS budget. They all route through the controller's
  // `scheduleReplay`, which coalesces a burst into AT MOST ONE replay() (and `setOnline(true)` does the
  // same), so a few re-foreground cycles can no longer multiply drains.
  const onOnline = (): void => outboxController.setOnline(true);
  const onOffline = (): void => outboxController.setOnline(false);
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') {
      outboxController.setOnline(navigator.onLine);
      outboxController.scheduleReplay();
    }
  };
  const onFocusOrShow = (): void => outboxController.scheduleReplay();

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onFocusOrShow);
  window.addEventListener('pageshow', onFocusOrShow);

  // Fallback poll (see the doc comment on REPLAY_POLL_INTERVAL_MS above). Cheap when idle: both checks
  // are in-memory/native-property reads, so an empty queue never touches IndexedDB.
  const pollId = window.setInterval(() => {
    if (outboxController.getSnapshot().pending === 0) return;
    if (!navigator.onLine) return; // replay() would bail immediately anyway
    outboxController.scheduleReplay();
  }, REPLAY_POLL_INTERVAL_MS);

  // Best-effort durable storage (iOS eviction mitigation). Failure is fine — the outbox is a cache.
  if (navigator.storage?.persist) {
    void navigator.storage.persist().catch(() => undefined);
  }

  // Drain anything left over from a previous session on boot (if already online). The controller
  // only sends entries owned by the CURRENT member, so a leftover queue from someone else on this
  // shared device stays put until its owner signs back in. Coalesced with any boot-time foreground
  // burst into a single drain.
  outboxController.scheduleReplay();

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onFocusOrShow);
    window.removeEventListener('pageshow', onFocusOrShow);
    window.clearInterval(pollId);
    configureOutbox(null);
  };
}
